import { getAppVersion } from "./appRuntime";

/**
 * 判断当前版本号是否大于等于传入的版本号
 * @param targetVersion 目标版本号，例如 "1.0"、"1.0.0"、"1.1"、"1.0.1"、"1.2"
 * @returns Promise<boolean> 如果app版本号大于等于目标版本号返回true，否则返回false
 */
export const isAppVersionGreaterThanOrEqual = async (
  targetVersion: string
): Promise<boolean> => {
  try {
    const appVersion = await getAppVersion();

    // 解析app版本号
    const appVersionNumber = appVersion.replace(/[Vv]/g, "");
    const appVersionParts = appVersionNumber.split(".");
    const appMajorVersion = parseInt(appVersionParts[0]);
    const appMinorVersion = parseInt(appVersionParts[1] || "0");
    const appPatchVersion = parseInt(appVersionParts[2] || "0");
    const appVersionValue =
      appMajorVersion + appMinorVersion / 100 + appPatchVersion / 10000;

    // 解析目标版本号
    const targetVersionNumber = targetVersion.replace(/[Vv]/g, "");
    const targetVersionParts = targetVersionNumber.split(".");
    const targetMajorVersion = parseInt(targetVersionParts[0]);
    const targetMinorVersion = parseInt(targetVersionParts[1] || "0");
    const targetPatchVersion = parseInt(targetVersionParts[2] || "0");
    const targetVersionValue =
      targetMajorVersion +
      targetMinorVersion / 100 +
      targetPatchVersion / 10000;

    return appVersionValue >= targetVersionValue;
  } catch (error) {
    console.error("Failed to compare app version:", error);
    return false;
  }
};

// 从2.0.0开始，原生支持图片多选、连拍
export const kNativeMultiImagePickerVersion = "2.0.0";
// 从3.0.0开始，iOS原生支持获取定位
export const kIOSNativeLocationVersion = "3.0.0";
// 从4.0.0开始，安卓原生安全区域开始适配
export const kAndroidSafeAreaVersion = "4.0.0";
// 从7.0.0开始，双端支持导航
export const kNativeMapNavigationVersion = "7.0.0";
// 从7.1.0开始，双端支持原生扫码（当前仅限于派送成功前校验）
export const kNativeScanVersion = "7.1.0";
// 从8.2.0开始，派送app地图模式从原生迁回web (8.0.0 派送app地图模式迁移到原生，由于产品要求，进行回迁移)
export const kNativeMapModeVersion = "8.2.0";
// 从8.0.1开始，安卓谷歌导航原生跳转优化
export const kAndroidGoogleMapVersion = "8.0.1";
// 从8.5.0开始，iOS派送模块迁移原生
export const kIOSNativeDeliveryVersion = '8.5.0'
// 从8.1.0开始，安卓支持Waze导航
export const kAndroidWazeVersion = '8.1.0'
// 从8.6.0开始，iOS支持Waze导航
export const kIOSWazeVersion = '8.6.0'
//从8.7.0开始不将zipCode传递给客户端
export const kDoNotPassZipCodeListVersion = '8.7.0'
// 从8.8.0开始，双端登录、退登走native端
export const kNativeLoginVersion = '8.8.0'
// PDA 壳 1.5 开始支持水印功能
export const kPdaWatermarkVersion = '1.5'

/**
 * 检查当前设备是否支持Waze导航
 * Android版本号 >= 8.1.0 支持Waze
 * iOS版本号 >= 8.6.0 支持Waze
 * @returns Promise<boolean> 如果支持Waze导航返回true，否则返回false
 */
export const checkWazeSupport = async (): Promise<boolean> => {
    try {
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);

        if (isIOSDevice) {
            return await isAppVersionGreaterThanOrEqual(kIOSWazeVersion);
        } else {
            // 检查是否为Android设备
            const isAndroidDevice = /android/i.test(navigator.userAgent);
            if (isAndroidDevice) {
                return await isAppVersionGreaterThanOrEqual(kAndroidWazeVersion);
            }
        }

        // 其他平台（如Web浏览器）不支持
        return false;
    } catch (error) {
        console.error('Failed to check Waze support:', error);
        return false;
    }
};